import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import './Calendar.scss';
import { TripService } from '../../services/trip/tripService';
import ddLogger from '../../logging/ddLogger';
import { Spinner } from '../../assets/images';
import { CalendarSection } from './CalendarSection';
import _ from 'lodash';
import { Alert } from '../../views/extended-stay/ExtendedStay';
import { tripAvailability } from '../../types/trip.type';

interface CalendarProps {
    setCheckIn: (date: moment.Moment) => void;
    setCheckOut: (date: moment.Moment) => void;
    checkIn: string;
    checkOut: string;
    reservationId: string;
    setReservationId: (id: string) => void;
    clean: boolean;
    isLoading: boolean;
    setError: (value: Alert) => void;
}

interface CalendarState {
    isLoading: boolean;
    lastPossibleCheckIn: moment.Moment | null;
    lastPossibleCheckOut: moment.Moment | null;
}

const defaultState = {
    date: '',
    arr: [],
};

export type CalendarAvailability = Array<{ date: string; bookable: boolean }>;

export const Calendar: React.FC<CalendarProps> = (props) => {
    const initialStateCheckIn = useRef<{ date: string; arr: CalendarAvailability }>(defaultState);
    const initialStateCheckOut = useRef<{ date: string; arr: CalendarAvailability }>(defaultState);
    const [stateCheckIn, setStateCheckIn] = useState<{ date: string; arr: CalendarAvailability }>(defaultState);
    const [stateCheckOut, setStateCheckOut] = useState<{ date: string; arr: CalendarAvailability }>(defaultState);
    const [state, setState] = useState<CalendarState>({
        isLoading: true,
        lastPossibleCheckIn: null,
        lastPossibleCheckOut: null,
    });

    useEffect(() => {
        setStateCheckIn(initialStateCheckIn.current);
        setStateCheckOut(initialStateCheckOut.current);
    }, [props.clean]);

    useEffect(() => {
        (async () => {
            if (state.isLoading) {
                await getAvailability(props.reservationId);
                props.setReservationId(props.reservationId);
                setStateCheckIn(initialStateCheckIn.current);
                setStateCheckOut(initialStateCheckOut.current);
                setState({
                    ...state,
                    isLoading: false,
                });
            }
        })();
    }, []);

    const getAvailability = async (reservationId: string) => {
        let response: tripAvailability;
        try {
            ddLogger.info(`previewing adjustment extend-stay-availability for ${reservationId}`);
            const tripService = new TripService();
            response = await tripService.getAvailability(reservationId);
            console.debug('ExtendedStay', response);
            ddLogger.info(`adjustment extend-stay-availability successfully previewed`, {
                response,
            });
        } catch (e) {
            response = {
                type: 'trips-availability',
                id: '',
                attributes: {
                    id: '',
                    checkin: props.checkIn,
                    checkout: props.checkOut,
                    extended_checkins: [],
                    extended_checkouts: [],
                },
            };
            const code = e.code;
            ddLogger.error(`ERROR previewing adjustment extend-stay-availability for ${reservationId}`, {
                error: e.message,
                reservation_id: reservationId,
                code,
            });
            props.setError(Alert.WARNING);
        }
        const localCheckIn = moment(response.attributes.checkin);
        const localCheckOut = moment(response.attributes.checkout);

        props.setCheckIn(localCheckIn);
        props.setCheckOut(localCheckOut);

        response.attributes.extended_checkins.push({ date: localCheckIn.format('YYYY-MM-DD'), bookable: false });
        response.attributes.extended_checkouts.unshift({ date: localCheckOut.format('YYYY-MM-DD'), bookable: false });

        initialStateCheckIn.current = {
            date: response.attributes.checkin,
            arr: response.attributes.extended_checkins,
        };
        initialStateCheckOut.current = {
            date: response.attributes.checkout,
            arr: response.attributes.extended_checkouts,
        };
    };

    if (state.isLoading) {
        return (
            <div className={'loading'}>
                <Spinner />
            </div>
        );
    }

    return (
        <div className={'d-flex flex-column gap-48'}>
            <CalendarSection
                date={stateCheckIn.date}
                dates={stateCheckIn.arr}
                type={'check_in'}
                clean={props.clean}
                isLoading={props.isLoading}
                setDate={(date: moment.Moment) => props.setCheckIn(date)}
            />
            <CalendarSection
                date={stateCheckOut.date}
                dates={stateCheckOut.arr}
                type={'check_out'}
                clean={props.clean}
                isLoading={props.isLoading}
                setDate={(date: moment.Moment) => props.setCheckOut(date)}
            />
        </div>
    );
};
